import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Select from '../../../components/ui/Select';
import { Checkbox } from '../../../components/ui/Checkbox';

const ExportOptionsModal = ({ onClose, onExport }) => {
  const [exportOptions, setExportOptions] = useState({
    format: 'pdf',
    includeCharts: true,
    includeTables: true,
    includeSummary: true,
    pageOrientation: 'portrait',
    pageSize: 'a4',
    includeHeader: true,
    includeFooter: true,
    includeLogo: true
  });

  const formatOptions = [
    { value: 'pdf', label: 'PDF', icon: 'FileText', description: 'Formato ideal para impressão e compartilhamento' },
    { value: 'excel', label: 'Excel', icon: 'FileSpreadsheet', description: 'Permite análise e edição de dados' },
    { value: 'csv', label: 'CSV', icon: 'FileCode', description: 'Formato universal para importação' }
  ];

  const orientationOptions = [
    { value: 'portrait', label: 'Retrato' },
    { value: 'landscape', label: 'Paisagem' }
  ];

  const pageSizeOptions = [
    { value: 'a4', label: 'A4' },
    { value: 'letter', label: 'Carta' },
    { value: 'legal', label: 'Ofício' }
  ];

  const handleOptionChange = (field, value) => {
    setExportOptions(prev => ({ ...prev, [field]: value }));
  };

  const handleExport = () => {
    onExport(exportOptions);
    onClose();
  };

  const getFormatIcon = (format) => {
    const formatData = formatOptions?.find(f => f?.value === format);
    return formatData ? formatData?.icon : 'FileText';
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-[1030] flex items-center justify-center p-4">
      <div className="bg-card rounded-lg shadow-xl w-full max-w-2xl">
        <div className="p-6 border-b border-border flex items-center justify-between">
          <div>
            <h2 className="text-xl font-semibold text-foreground">Opções de Exportação</h2>
            <p className="text-sm text-muted-foreground mt-1">Configure o formato e conteúdo do relatório</p>
          </div>
          <button
            onClick={onClose}
            className="p-2 rounded-md hover:bg-muted transition-colors"
          >
            <Icon name="X" size={20} />
          </button>
        </div>

        <div className="p-6 space-y-6">
          <div>
            <label className="block text-sm font-medium text-foreground mb-3">Formato de Exportação</label>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              {formatOptions?.map((format) => (
                <button
                  key={format?.value}
                  onClick={() => handleOptionChange('format', format?.value)}
                  className={`p-4 rounded-lg border-2 transition-all text-left ${
                    exportOptions?.format === format?.value
                      ? 'border-primary bg-primary/5' :'border-border hover:border-primary/50'
                  }`}
                >
                  <div className="flex items-center gap-3 mb-2">
                    <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                      exportOptions?.format === format?.value ? 'bg-primary/10' : 'bg-muted'
                    }`}>
                      <Icon 
                        name={format?.icon} 
                        size={20} 
                        className={exportOptions?.format === format?.value ? 'text-primary' : 'text-muted-foreground'}
                      />
                    </div>
                    <span className="text-sm font-semibold text-foreground">{format?.label}</span>
                  </div>
                  <p className="text-xs text-muted-foreground">{format?.description}</p>
                </button>
              ))}
            </div>
          </div>

          {exportOptions?.format === 'pdf' && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Select
                label="Orientação da Página"
                options={orientationOptions}
                value={exportOptions?.pageOrientation}
                onChange={(value) => handleOptionChange('pageOrientation', value)}
              />
              <Select
                label="Tamanho da Página"
                options={pageSizeOptions}
                value={exportOptions?.pageSize}
                onChange={(value) => handleOptionChange('pageSize', value)}
              />
            </div>
          )}

          <div className="space-y-3 p-4 bg-muted/50 rounded-lg">
            <p className="text-sm font-medium text-foreground mb-3">Conteúdo do Relatório</p>
            <Checkbox
              label="Incluir Gráficos"
              description="Adiciona visualizações gráficas ao relatório"
              checked={exportOptions?.includeCharts}
              onChange={(e) => handleOptionChange('includeCharts', e?.target?.checked)}
            />
            <Checkbox
              label="Incluir Tabelas Detalhadas"
              description="Adiciona tabelas com dados completos"
              checked={exportOptions?.includeTables}
              onChange={(e) => handleOptionChange('includeTables', e?.target?.checked)}
            />
            <Checkbox
              label="Incluir Resumo Executivo"
              description="Adiciona análise textual e insights"
              checked={exportOptions?.includeSummary}
              onChange={(e) => handleOptionChange('includeSummary', e?.target?.checked)}
            />
          </div>

          {exportOptions?.format === 'pdf' && (
            <div className="space-y-3 p-4 bg-muted/50 rounded-lg">
              <p className="text-sm font-medium text-foreground mb-3">Elementos da Página</p>
              <Checkbox
                label="Incluir Cabeçalho"
                description="Adiciona cabeçalho com título e data"
                checked={exportOptions?.includeHeader}
                onChange={(e) => handleOptionChange('includeHeader', e?.target?.checked)}
              />
              <Checkbox
                label="Incluir Rodapé"
                description="Adiciona rodapé com numeração de páginas"
                checked={exportOptions?.includeFooter}
                onChange={(e) => handleOptionChange('includeFooter', e?.target?.checked)}
              />
              <Checkbox
                label="Incluir Logo da Empresa"
                description="Adiciona logo JR Wise Tour ao relatório"
                checked={exportOptions?.includeLogo}
                onChange={(e) => handleOptionChange('includeLogo', e?.target?.checked)}
              />
            </div>
          )}

          <div className="p-4 bg-accent/10 rounded-lg flex items-start gap-3">
            <Icon name="Info" size={20} className="text-accent flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-sm font-medium text-foreground mb-1">Dica de Exportação</p>
              <p className="text-sm text-muted-foreground">
                {exportOptions?.format === 'pdf' && 'PDF é ideal para compartilhamento e impressão com formatação preservada.'}
                {exportOptions?.format === 'excel' && 'Excel permite análise avançada e manipulação dos dados exportados.'}
                {exportOptions?.format === 'csv' && 'CSV é compatível com a maioria dos sistemas e ferramentas de análise.'}
              </p>
            </div>
          </div>
        </div>

        <div className="p-6 border-t border-border flex gap-3">
          <Button variant="outline" fullWidth onClick={onClose}>
            Cancelar
          </Button>
          <Button 
            variant="default" 
            fullWidth 
            iconName="Download" 
            iconPosition="left"
            onClick={handleExport}
          >
            Exportar {exportOptions?.format?.toUpperCase()}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default ExportOptionsModal;